// /web/src/pages/projects/CalculatorsHub.jsx
import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

export default function CalculatorsHub() {
  const { id } = useParams();
  const navigate = useNavigate();

  if (!id) {
    navigate("/dashboard");
    return null;
  }

  const calculators = [
    {
      key: "lifts",
      name: "Lifts",
      path: `/projects/${id}/calculators/lifts`,
      ready: true,
      blurb: "Traffic analysis & optimal arrangement.",
      costTokens: 5,
    },
    {
      key: "wc",
      name: "WC Provision",
      path: `/projects/${id}/calculators/wc`,
      ready: true,
      blurb: "Male, female, unisex & accessible counts from GIA and occupancy.",
      costTokens: 1,
    },
    {
      key: "stairs",
      name: "Staircase Provision",
      path: `/projects/${id}/calculators/stairs`,
      ready: true,
      blurb: "Stair count and minimum widths from occupant loads.",
      costTokens: 1,
    },
    {
      key: "risers",
      name: "Riser Calculator",
      path: `/projects/${id}/calculators/risers?project_id=${id}`,
      ready: true,
      blurb: "Preliminary riser schedule and shaft allowance by floor.",
      costTokens: 1,
    },
    {
      key: "u-values",
      name: "U-value Calculator",
      path: `/projects/${id}/calculators/u-values`,
      ready: true,
      blurb: "Build-up layers, get U-value & Part L check.",
      costTokens: 1,
    },
    {
      key: "cycles",
      name: "Cycle Spaces",
      path: `/calculators/cycles?project_id=${id}`,
      ready: true,
      blurb: "Calculated based on Local Planning Authority requirements.",
      costTokens: 1,
    },
    { key: "plant", name: "Plant", path: "#", ready: false, blurb: "Coming soon." },
    { key: "eot", name: "End of Trip", path: "#", ready: false, blurb: "Coming soon." },
  ];

  return (
    <div className="container" style={{ padding: "28px 0 40px" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0 }}>Calculators</h1>
          <div className="small" style={{ marginTop: 6 }}>Choose a calculator for this project.</div>
        </div>
        <Link className="btn btn--ghost" to={`/projects/${id}`}>Back to project</Link>
      </header>

      <section style={{ marginTop: 16 }}>
        <div style={grid}>
          {calculators.map((c, idx) => (
            <div
              key={c.key}
              style={card(c.ready, idx)}
              onMouseEnter={c.ready ? (e) => lift(e.currentTarget, true) : undefined}
              onMouseLeave={c.ready ? (e) => lift(e.currentTarget, false) : undefined}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <h4 style={{ margin: 0 }}>{c.name}</h4>
                <span style={pill(c.ready, idx)}>{c.ready ? "Live" : "Soon"}</span>
              </div>
              <div className="small" style={{ opacity: 0.75 }}>
                {c.blurb}
              </div>
              {c.ready && Number.isFinite(c.costTokens) && (
                <div className="small" style={{ opacity: 0.7 }}>
                  Cost: {c.costTokens} token{c.costTokens === 1 ? "" : "s"} per run
                </div>
              )}
              <div style={{ marginTop: "auto" }}>
                {c.ready ? (
                  <Link className="btn btn--primary" to={c.path}>Open</Link>
                ) : (
                  <button className="btn" disabled>Open</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 16,
};

const PALETTE = [
  "#286085", // Deep blue
  "#3f8ea2", // Teal
  "#62a56f", // Green
  "#dfc650", // Mustard
  "#e48b41", // Orange
  "#cc4140", // Red
  "#b8375d", // Magenta
  "#714584", // Purple
  "#5b4c93", // Violet
];

const card = (ready, idx = 0) => {
  if (!ready) {
    return {
      border: "1px dashed var(--line, rgba(0,0,0,0.18))",
      borderRadius: 0,
      padding: 16,
      background: "rgba(0,0,0,0.02)",
      boxShadow: "none",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    };
  }

  const col = PALETTE[idx % PALETTE.length];
  return {
    borderLeft: `4px solid ${col}`,
    borderRadius: 0,
    padding: 16,
    background: `linear-gradient(0deg, ${col}14, ${col}14)`,
    boxShadow: "0 0 0 rgba(0,0,0,0)",
    transition: "transform 160ms ease, box-shadow 160ms ease",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    willChange: "transform",
  };
};

const lift = (el, active) => {
  if (!el) return;
  el.style.transform = active ? "translateY(-2px)" : "translateY(0)";
  el.style.boxShadow = active
    ? "0 8px 20px rgba(0,0,0,0.08)"
    : "0 0 0 rgba(0,0,0,0)";
};

const pill = (ready, idx = 0) => {
  if (!ready) {
    return {
      fontSize: 11,
      padding: "4px 8px",
      borderRadius: 999,
      border: "1px solid var(--line, rgba(0,0,0,0.2))",
      background: "rgba(0,0,0,0.06)",
      letterSpacing: 0.4,
      textTransform: "uppercase",
      fontWeight: 600,
      opacity: 0.9,
    };
  }

  const col = PALETTE[idx % PALETTE.length];
  return {
    fontSize: 11,
    padding: "4px 8px",
    borderRadius: 999,
    border: `1px solid ${col}55`,
    background: `${col}1a`,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    fontWeight: 600,
    opacity: 0.9,
  };
};
